/**
 * FactoryJourneyDefinition (AJIP-16.02.009 · §9)
 * ---------------------------------------------------------------------
 * Enriched v2 Factory Journey canonical definition. Registered via
 * RegistrationService.replace() by FactoryJourneyOrchestrator over the
 * v1 canonical registered in canonicalDefinitions.js.
 *
 * Founder Rulings honoured:
 *   F1 — Requires JOURNEYS_ENABLED AND JOURNEYS_FACTORY.
 *   F2 — journeyId is 'factory' (retained precedent; no competing key).
 *   F3 — journeyVersion is integer 2 (RD-03).
 *   F4 — Single-objective per (organisationRef, factorySubjectRef);
 *        eligibility rule `no-reentry-after:completed` for the same
 *        organisation-factory pair. Multiple principals under one
 *        organisation share one instance (identity policy — see
 *        FactoryJourneyOrchestrator).
 *   F5 — Journey Statuses map to certified STATES.
 *   F6 — Identity inputs referenced here for context contract only;
 *        deterministic instance identity is derived in the orchestrator.
 *
 * Directive corrections honoured:
 *   §5.7 — Feature-flag containment via requiredFeatureFlags.
 *   §5.8 — Foundation defines evidence readiness only, never
 *          verification truth.
 *   §22 — Buyer Journey behaviour unchanged; the definition carries
 *         `conflicts-with:buyer` symmetric with buyer/supplier.
 *   §21 — Supplier Journey remains separate; `conflicts-with:supplier`
 *         mirrors the symmetry established in AJIP-16.02.008.
 */
import { STATES } from '../core/JourneyState';

const buildFactoryDefinition = ({ version = 2, nowFn = () => new Date().toISOString() } = {}) => ({
  journeyId: 'factory',
  journeyVersion: version,
  journeyName: 'Factory Journey',
  journeyDescription:
    'Governed factory foundation pathway: identity → profile → capability declaration → evidence readiness → operational readiness → foundation ready. Single-objective per (organisation, factory subject).',
  journeyType: 'factory',
  journeyCategory: 'operational',
  journeyAudience: ['authenticated', 'authorised-factory'],
  registrationStatus: 'registered',
  // F1 — Both flags required at runtime.
  requiredFeatureFlags: ['JOURNEYS_ENABLED', 'JOURNEYS_FACTORY'],
  entryConditions: [],
  // F4 — one active journey per organisation-factory pair.
  // §22 / §21 — separation from Buyer and Supplier journey instances.
  eligibilityRules: [
    'no-reentry-after:completed',
    'conflicts-with:buyer',
    'conflicts-with:supplier',
  ],
  // Correction 2 · §5 principle 5.5: permissions evaluated at runtime.
  permissionRequirements: [],
  initialState: STATES.NOT_STARTED,
  supportedStates: [
    STATES.NOT_STARTED, STATES.STARTED, STATES.IN_PROGRESS,
    STATES.WAITING, STATES.BLOCKED, STATES.COMPLETED,
    STATES.CANCELLED, STATES.FAILED,
  ],
  transitionConfiguration: [
    { from: STATES.STARTED, event: 'proceed', to: STATES.IN_PROGRESS },
    { from: STATES.IN_PROGRESS, event: 'wait', to: STATES.WAITING },
    { from: STATES.WAITING, event: 'resume', to: STATES.IN_PROGRESS },
    { from: STATES.IN_PROGRESS, event: 'complete', to: STATES.COMPLETED },
    { from: STATES.IN_PROGRESS, event: 'cancel', to: STATES.CANCELLED },
    { from: STATES.IN_PROGRESS, event: 'fail', to: STATES.FAILED },
    { from: STATES.WAITING, event: 'cancel', to: STATES.CANCELLED },
    { from: STATES.STARTED, event: 'cancel', to: STATES.CANCELLED },
  ],
  persistencePolicyRef: 'factory:v2:standard',
  retentionPolicyRef: 'factory:v2:standard',
  priority: 40,
  tags: ['factory', 'operational', 'ajip-16.02.009'],
  canonReference: 'AJIP-16.02.009',
  owner: 'Founder',
  createdAt: nowFn(),
  updatedAt: nowFn(),
  deprecationStatus: 'none',
  replacementJourneyId: null,
});

export { buildFactoryDefinition };
